import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Mail, FolderKanban, ArrowRight, Sparkles, LogOut } from "lucide-react";
import { useAuth } from "../../App";
import { Button } from "../../components/ui/button";

/**
 * Espace selector shown on every dashboard entry.
 * Two tiles: ALIA Mail / ALIA Projets.
 * No persistence — we want the user to choose each session.
 */
const DashboardSelectPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Avoid keeping any stale "last space" memory
  useEffect(() => {
    try { sessionStorage.removeItem("alia-last-space"); } catch { /* private mode */ }
  }, []);

  const tiles = [
    {
      key: "mail",
      title: "ALIA Mail",
      tagline: "Catégorisation IA + actions rapides sur votre boîte Outlook",
      icon: Mail,
      gradient: "from-blue-600 via-blue-500 to-indigo-600",
      ring: "ring-blue-500/30",
      to: "/dashboard/inbox",
      bullets: [
        "Urgent / À répondre / À déléguer / Info — classés automatiquement",
        "Drawer email avec actions contextuelles selon la catégorie",
        "50 derniers emails Outlook, en 1 clic"
      ]
    },
    {
      key: "projects",
      title: "ALIA Projets",
      tagline: "Pilotage des missions client : phases, livrables, suivi",
      icon: FolderKanban,
      gradient: "from-purple-600 via-fuchsia-500 to-pink-600",
      ring: "ring-purple-500/30",
      to: "/dashboard/projects",
      bullets: [
        "13 livrables structurés par projet",
        "Phases & Tâches centralisés",
        "Vue d'ensemble multi-projets"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950" data-testid="dashboard-select-page">
      {/* Top bar */}
      <header className="border-b border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/60 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center">
            <img src="/logo-alia.png" style={{height: "40px", objectFit: "contain"}} alt="ALIA Assistant" />
          </div>
          <div className="flex items-center gap-3">
            {user?.email && (
              <span className="hidden sm:inline text-xs text-slate-500 dark:text-slate-400 truncate max-w-[200px]">
                {user.email}
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              data-testid="select-logout-btn"
              className="min-h-[40px]"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-6 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs font-medium mb-4">
          <Sparkles className="w-3.5 h-3.5" />
          Bienvenue {user?.name?.split(' ')[0] || ''}
        </div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit' }}>
          Quel espace voulez-vous ouvrir&nbsp;?
        </h1>
        <p className="mt-3 text-sm sm:text-base text-slate-500 dark:text-slate-400 max-w-xl mx-auto">
          Choisissez l'espace adapté à votre tâche du moment. Vous pourrez basculer
          à tout moment depuis le bouton « Changer d'espace ».
        </p>
      </div>

      {/* Tiles */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => navigate(t.to)}
              data-testid={`tile-${t.key}`}
              className={`group relative text-left rounded-3xl p-6 sm:p-8 bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 hover:shadow-xl hover:-translate-y-0.5 transition-all min-h-[300px] ring-0 hover:ring-4 ${t.ring}`}
            >
              <div className={`absolute inset-x-0 top-0 h-1.5 rounded-t-3xl bg-gradient-to-r ${t.gradient}`} />
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${t.gradient} text-white flex items-center justify-center shadow-lg`}>
                <Icon className="w-7 h-7" />
              </div>
              <h2 className="mt-5 text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit' }}>
                {t.title}
              </h2>
              <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">{t.tagline}</p>
              <ul className="mt-5 space-y-2">
                {t.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                    <span className={`mt-1.5 w-1.5 h-1.5 rounded-full bg-gradient-to-r ${t.gradient} flex-shrink-0`} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-slate-900 dark:text-white group-hover:gap-3 transition-all">
                Ouvrir {t.title}
                <ArrowRight className="w-4 h-4" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DashboardSelectPage;
